import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ImageFile } from "components/ImageUpload";
import { Meta, PortfolioStatistics } from "entities/common";
import { DeviceStatistics } from "entities/devices";
import {
  CreatePortfolioRequest,
  PortfolioComplianceScore,
  PortfolioIAQDashboard,
  PortfolioItem,
  UpdatePortfolioRequest,
} from "entities/portfolios";

import { SilentMeta } from "store/silent";

export type CreatePortfolioRequestPayload = {
  body: CreatePortfolioRequest;
  images?: ImageFile[];
};

export type UpdatePortfolioRequestPayload = {
  id: number;
  body: UpdatePortfolioRequest;
  newImages?: ImageFile[];
  removedImageIds?: number[];
};

type ModalState = {
  items: PortfolioItem[];
  current: PortfolioItem | null;
  statistics: PortfolioStatistics | null;
  portfolioDeviceStatistic: DeviceStatistics | null;
  isPortfolioDeviceStatisticLoading: boolean;
  isLoading: boolean;
  isCurrentLoading: boolean;
  isStatisticsLoading: boolean;
  meta: Meta;
  errors: { detail: Record<string, string> } | null;
  successfullyAddedId: number | null;
  successfullyUpdatedId: number | null;
  successfullyRemovedId: number | null;
  complianceScore: PortfolioComplianceScore | null;
  isComplianceScoreLoading: boolean;
  iaqDashboard: PortfolioIAQDashboard | null;
  isIAQDashboardLoading: boolean;
  complianceScoreTrend: unknown | null;
  isComplianceScoreTrendLoading: boolean;
};

const initialState: ModalState = {
  items: [],
  current: null,
  statistics: null,
  isLoading: false,
  isCurrentLoading: false,
  isStatisticsLoading: false,
  meta: {
    total: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
  },
  errors: null,
  successfullyAddedId: null,
  successfullyUpdatedId: null,
  successfullyRemovedId: null,
  complianceScore: null,
  isComplianceScoreLoading: false,
  iaqDashboard: null,
  isIAQDashboardLoading: false,
  complianceScoreTrend: null,
  isComplianceScoreTrendLoading: false,
  portfolioDeviceStatistic: null,
  isPortfolioDeviceStatisticLoading: false,
};

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    getPortfolioListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isLoading = true;
      },
      prepare: (query = "", meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getPortfolioListSuccess: (state, action: PayloadAction<{ items: PortfolioItem[] } & Meta>) => {
      state.items = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages,
      };
      state.isLoading = false;
    },
    getPortfolioListFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    getPortfolioRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isCurrentLoading = true;
    },
    getPortfolioSuccess: (state, action: PayloadAction<PortfolioItem>) => {
      state.current = action.payload;
      state.isCurrentLoading = false;
    },
    getPortfolioFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isCurrentLoading = false;
    },

    getPortfolioStatisticsRequest: {
      reducer(state, action: PayloadAction<undefined, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticsLoading = true;
      },
      prepare: (meta: SilentMeta = {}) => ({ payload: undefined, meta }),
    },
    getPortfolioStatisticsSuccess: (state, action: PayloadAction<PortfolioStatistics>) => {
      state.statistics = action.payload;
      state.isStatisticsLoading = false;
    },
    getPortfolioStatisticsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isStatisticsLoading = false;
    },

    getComplianceScoreRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isComplianceScoreLoading = true;
    },
    getComplianceScoreSuccess: (state, action: PayloadAction<PortfolioComplianceScore>) => {
      state.complianceScore = action.payload;
      state.isComplianceScoreLoading = false;
    },
    getComplianceScoreFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isComplianceScoreLoading = false;
    },

    getIAQDashboardRequest: {
      reducer(state, action: PayloadAction<Record<string, unknown>, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isIAQDashboardLoading = true;
      },
      prepare: (params: Record<string, unknown>, meta: SilentMeta = {}) => ({ payload: params, meta }),
    },
    getIAQDashboardSuccess: (state, action: PayloadAction<PortfolioIAQDashboard>) => {
      state.iaqDashboard = action.payload;
      state.isIAQDashboardLoading = false;
    },
    getIAQDashboardFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isIAQDashboardLoading = false;
    },

    getPortfolioDeviceStatisticsRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isPortfolioDeviceStatisticLoading = true;
      },
      prepare: (query: string, meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getPortfolioDeviceStatisticsSuccess: (state, action: PayloadAction<DeviceStatistics>) => {
      state.portfolioDeviceStatistic = action.payload;
      state.isPortfolioDeviceStatisticLoading = false;
    },
    getPortfolioDeviceStatisticsFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isPortfolioDeviceStatisticLoading = false;
    },

    createPortfolioRequest: (state, action: PayloadAction<CreatePortfolioRequestPayload>) => {
      void action.payload;
      state.isLoading = true;
      state.successfullyAddedId = null;
    },
    createPortfolioSuccess: (state, action: PayloadAction<PortfolioItem>) => {
      state.successfullyAddedId = action.payload.id;
      state.isLoading = false;
    },
    createPortfolioFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    updatePortfolioRequest: (state, action: PayloadAction<UpdatePortfolioRequestPayload>) => {
      void action.payload;
      state.isLoading = true;
      state.successfullyUpdatedId = null;
    },
    updatePortfolioSuccess: (state, action: PayloadAction<PortfolioItem>) => {
      state.current = action.payload;
      state.isLoading = false;
      state.successfullyUpdatedId = action.payload.id;
    },
    updatePortfolioFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    removePortfolioRequest: (state, action: PayloadAction<number>) => {
      void action.payload;
      state.isLoading = true;
      state.successfullyRemovedId = null;
    },
    removePortfolioSuccess: (state, action: PayloadAction<number>) => {
      state.isLoading = false;
      state.successfullyRemovedId = action.payload;
    },
    removePortfolioFailure: (state, action: PayloadAction<{ detail: Record<string, string> }>) => {
      state.errors = action.payload;
      state.isLoading = false;
    },

    changePage: (state, action: PayloadAction<number>) => {
      state.meta.page = action.payload;
    },
    resetSuccessfullyAddedPortfolio: (state) => {
      state.successfullyAddedId = null;
    },
    resetSuccessfullyUpdatedPortfolio: (state) => {
      state.successfullyUpdatedId = null;
    },
    resetSuccessfullyRemovedPortfolio: (state) => {
      state.successfullyRemovedId = null;
    },
    resetErrorsPortfolio: (state) => {
      state.errors = null;
    },
    resetPortfolioList: (state) => {
      state.items = [];
      state.meta = {
        total: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
      };
    },
    resetCurrentPortfolio: (state) => {
      state.current = null;
    },
    resetPortfolioStatistics: (state) => {
      state.statistics = null;
    },
    resetPortfolioDeviceStatistic: (state) => {
      state.portfolioDeviceStatistic = null;
    },
  },
});

export const {
  getPortfolioListRequest,
  getPortfolioListSuccess,
  getPortfolioListFailure,
  getPortfolioRequest,
  getPortfolioSuccess,
  getPortfolioFailure,
  getPortfolioStatisticsRequest,
  getPortfolioStatisticsSuccess,
  getPortfolioStatisticsFailure,
  getComplianceScoreRequest,
  getComplianceScoreSuccess,
  getComplianceScoreFailure,
  getIAQDashboardRequest,
  getIAQDashboardSuccess,
  getIAQDashboardFailure,
  getPortfolioDeviceStatisticsRequest,
  getPortfolioDeviceStatisticsSuccess,
  getPortfolioDeviceStatisticsFailure,
  createPortfolioRequest,
  createPortfolioSuccess,
  createPortfolioFailure,
  updatePortfolioRequest,
  updatePortfolioSuccess,
  updatePortfolioFailure,
  removePortfolioRequest,
  removePortfolioSuccess,
  removePortfolioFailure,
  changePage,
  resetSuccessfullyAddedPortfolio,
  resetSuccessfullyUpdatedPortfolio,
  resetSuccessfullyRemovedPortfolio,
  resetErrorsPortfolio,
  resetPortfolioList,
  resetCurrentPortfolio,
  resetPortfolioStatistics,
  resetPortfolioDeviceStatistic,
} = portfolioSlice.actions;
export default portfolioSlice.reducer;
